<template>
  <div
    class="overflow-hidden relative flex-shrink-0 mx-3 mt-2 border-2 border-gray-200 transition-all duration-200 ease-in-out cursor-pointer select-none group hover:border-gray-400 first:mt-3.5 last:mb-20"
    :class="[{ 'bg-white border-blue-400': selected }, compact ? 'rounded-lg mb-1' : 'rounded-xl mb-3' ]"
    @click.prevent="onSelectNote(note)"
  >
    <div class="overflow-hidden leading-tight text-gray-800 whitespace-nowrap overflow-ellipsis"
      :class="[ compact ? 'px-3 py-2 font-normal' : 'pt-5 pr-12 pl-6 font-medium' ]"
      v-html="highlightText(note.title)"></div>
    <div v-if="!note.archived" @click.stop="pinNote(!note.pinned)" class="absolute opacity-0 group btn-icon-circle group-hover:opacity-75" :class="[{ 'text-black' : selected }, compact ? 'top-0.5 right-0.5 w-8 h-8' : 'top-2.5 right-2.5' ]" :title="note.pinned ? 'Unpin Note' : 'Pin Note'">
      <img v-if="note.pinned" class="opacity-75" src="../assets/icons/note/icon-pinned.svg" alt="Pinned" width="24" height="24"/>
      <img v-else class="opacity-75" src="../assets/icons/note/icon-pin.svg" alt="Pin" width="24" height="24"/>
    </div>
    <p v-show="!compact" class="overflow-hidden px-6 pb-3 my-2 max-w-full h-11 text-sm leading-normal word-break" :class="[ selected ? 'text-gray-800' : 'text-gray-500' ]" v-html="highlightText(noteContent(note))"></p>
    <div v-show="!compact" class="px-6 pb-5 text-xs text-gray-400">
      <span class="text-blue-700" v-if="note.endDate">{{ formatDate(note.endDate) }}</span>
      <span title="Last edited">{{ note.endDate ? ' &bull; ' : '' }} {{ fromNow(note.updatedAt || note.createdAt) }}</span>
    </div>
  </div>
</template>

<script>
import { endOfYear, format, formatDistanceToNowStrict, isBefore, parseISO, subMonths, subYears } from 'date-fns'
import { highlight, matches, snippet as windowAround } from '@/lib/search'

export default {
  name: 'NoteItem',
  props: {
    note: Object,
    selectedNoteId: {
      type: String,
      default: ''
    },
    compact: {
      type: Boolean,
      default: false
    },
    query: {
      type: String,
      default: ''
    },
    // text around the match, for a note the server matched on its full body
    snippet: {
      type: String,
      default: ''
    },
    onSelectNote: Function
  },
  computed: {
    selected() {
      return this.selectedNoteId && this.note._id === this.selectedNoteId
    }
  },
  methods: {
    pinNote(bool) {
      const data = { pinned: bool }
      if (!bool && this.note.pinnedOrder !== null) {
        data.pinnedOrder = null
      }
      this.note.patch({ data: data })
    },
    // Marks every term the user typed. Used to cut a window around the match
    // itself, which duplicated what the preview already does and truncated
    // titles mid-word; the preview text arrives windowed now.
    highlightText(text) {
      return highlight(text, this.query)
    },
    // The preview reads note.excerpt, which the server derives on save. It used
    // to strip tags out of note.content here, which meant this list could not
    // be drawn without downloading every note's full body.
    //
    // While searching it shows the text around the match instead, so a note
    // matched deep in its body does not preview an opening that contains none
    // of the searched words. The server sends that window for notes it matched;
    // a body already in the store is windowed here.
    //
    // content is preferred when it happens to be loaded -- an open note then
    // previews its unsaved edits rather than the last saved excerpt.
    noteContent(note) {
      const source = typeof note.content === 'string' ? note.content : (note.excerpt || '')
      const text = source.replace(/<(?:.|\n)*?>/gm, ' ')
      if (!this.query) return text.substring(0, 400)
      // the local text wins when it actually contains the match: it reflects
      // unsaved edits, where the server's snippet is of the last saved body
      if (matches(text, this.query)) return windowAround(text, this.query)
      return this.snippet || windowAround(text, this.query)
    },
    formatDate(date) {
      return format(parseISO(date), 'dd MMM yyyy')
    },
    fromNow(time) {
      const parsed = parseISO(time)
      if (isBefore(parsed, endOfYear(subYears(new Date(), 1)))) {
        return format(parsed, 'dd MMM yyyy')
      } else if (isBefore(parsed, subMonths(new Date(), 1))) {
        return format(parsed, 'dd MMM')
      } else {
        return formatDistanceToNowStrict(parsed)
          .replace(/\s\w+$/, unit => ({
            ' second': 's',
            ' seconds': 's',
            ' minute': 'm',
            ' minutes': 'm',
            ' hour': 'h',
            ' hours': 'h',
            ' day': 'd',
            ' days': 'd',
            ' month': 'mth'
          }[unit]))
      }
    }
  }
}
</script>
